import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Note: categories are derived dynamically from question data via getCategories()
// This legacy constant is kept for any external references but is not authoritative
export const CATEGORIES = [];

export const DIFFICULTIES = ['easy', 'medium', 'hard'];

// Transform raw JSON question into app format
function normalizeQuestion(raw, index) {
  const id = raw.id || `Q_${index}`;
  const options = raw.options && raw.options.length > 0
    ? raw.options
    : ['Option A', 'Option B', 'Option C', 'Option D'];

  let correctIndex = 0;
  if (typeof raw.correctIndex === 'number') {
    correctIndex = raw.correctIndex;
  } else if (raw.correct_answer) {
    const ans = raw.correct_answer.trim();
    // Check if it's a letter (A-E)
    if (/^[A-Ea-e]$/.test(ans)) {
      correctIndex = ans.toUpperCase().charCodeAt(0) - 65;
    } else {
      // Try to find the answer text in options
      const idx = options.findIndex(o => o === ans || o.toLowerCase() === ans.toLowerCase());
      correctIndex = idx >= 0 ? idx : 0;
    }
  }

  return {
    id,
    question: raw.question || '',
    options,
    correctIndex: Math.min(correctIndex, options.length - 1),
    explanation: raw.explanation || '',
    category: raw.category || 'Anatomy, Pathology & Research',
    difficulty: (raw.difficulty || 'medium').charAt(0).toUpperCase() + (raw.difficulty || 'medium').slice(1).toLowerCase(),
    source: raw.source || '',
    speaker: raw.speaker || '',
    year: raw.year || '',
    pmid: raw.pmid || '',
    tier: raw.tier || '',
  };
}

const useQuestionStore = create(
  persist(
    (set, get) => ({
      questions: [],
      loaded: false,
      bankVersion: null, // hash of the bank to detect updates
      userAnswers: {},
      flaggedQuestions: new Set(),
      confidenceRatings: {}, // { [questionId]: 'know' | 'kinda' | 'unsure' }

      setConfidence: (questionId, rating) => {
        set((state) => ({
          confidenceRatings: {
            ...state.confidenceRatings,
            [questionId]: rating,
          },
        }));
      },

      getConfidenceStats: () => {
        const { questions, confidenceRatings } = get();
        let know = 0, kinda = 0, unsure = 0, unrated = 0;
        questions.forEach((q) => {
          const r = confidenceRatings[q.id];
          if (r === 'know') know++;
          else if (r === 'kinda') kinda++;
          else if (r === 'unsure') unsure++;
          else unrated++;
        });
        return { know, kinda, unsure, unrated };
      },

      loadQuestionsFromJSON: async () => {
        // Always fetch fresh questions from the server (never rely on cached loaded state)
        try {
          const res = await fetch('/data/question_bank.json');
          if (!res.ok) throw new Error('Failed to load question bank');
          const json = await res.json();
          const data = Array.isArray(json) ? json.filter(q => q.question && q.question.length > 10) : [];
          const normalized = data.map((q, i) => normalizeQuestion(q, i));

          // Compute a simple version hash from question count + first/last IDs
          const newVersion = `${data.length}-${data[0]?.id || ''}-${data[data.length - 1]?.id || ''}`;
          const oldVersion = get().bankVersion;

          if (oldVersion && oldVersion !== newVersion) {
            console.log(`[EndoBoard] Bank updated: ${oldVersion} → ${newVersion}`);
            // Clean up orphaned progress (answers for questions no longer in the bank)
            const validIds = new Set(normalized.map(q => q.id));
            const { userAnswers, flaggedQuestions, confidenceRatings } = get();

            const cleanedAnswers = {};
            Object.entries(userAnswers).forEach(([id, val]) => {
              if (validIds.has(id)) cleanedAnswers[id] = val;
            });

            const cleanedFlags = new Set();
            flaggedQuestions.forEach(id => {
              if (validIds.has(id)) cleanedFlags.add(id);
            });

            const cleanedConfidence = {};
            Object.entries(confidenceRatings).forEach(([id, val]) => {
              if (validIds.has(id)) cleanedConfidence[id] = val;
            });

            const orphaned = Object.keys(userAnswers).length - Object.keys(cleanedAnswers).length;
            if (orphaned > 0) {
              console.log(`[EndoBoard] Cleaned ${orphaned} orphaned progress entries`);
            }

            set({
              questions: normalized,
              loaded: true,
              bankVersion: newVersion,
              userAnswers: cleanedAnswers,
              flaggedQuestions: cleanedFlags,
              confidenceRatings: cleanedConfidence,
            });
          } else {
            set({ questions: normalized, loaded: true, bankVersion: newVersion });
          }
        } catch (e) {
          console.error('Failed to load questions:', e);
          set({ loaded: true });
        }
      },

      answerQuestion: (questionId, selectedIndex) => {
        const q = get().questions.find((q) => q.id === questionId);
        if (!q) return;
        const isCorrect = selectedIndex === q.correctIndex;
        set((state) => ({
          userAnswers: {
            ...state.userAnswers,
            [questionId]: { selectedIndex, isCorrect, timestamp: Date.now() },
          },
        }));
        return isCorrect;
      },

      toggleFlag: (questionId) => {
        set((state) => {
          const flags = new Set(state.flaggedQuestions);
          if (flags.has(questionId)) flags.delete(questionId);
          else flags.add(questionId);
          return { flaggedQuestions: flags };
        });
      },

      clearSessionAnswers: (questionIds) => {
        set((state) => {
          const updated = { ...state.userAnswers };
          questionIds.forEach((id) => delete updated[id]);
          return { userAnswers: updated };
        });
      },

      // ── Export / Import progress ──
      exportProgress: () => {
        const { userAnswers, flaggedQuestions, confidenceRatings, bankVersion } = get();
        const progressData = JSON.parse(localStorage.getItem('endoboard-progress') || '{}');
        const backup = {
          _endoboard_backup: true,
          _version: 2,
          _exportedAt: new Date().toISOString(),
          _bankVersion: bankVersion,
          questions: {
            userAnswers,
            flaggedQuestions: Array.from(flaggedQuestions),
            confidenceRatings,
            bankVersion,
          },
          progress: progressData?.state || progressData,
        };
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `endoboard-backup-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      },

      importProgress: (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const backup = JSON.parse(e.target.result);
              if (!backup._endoboard_backup) {
                reject(new Error('Not a valid EndoBoard backup file'));
                return;
              }
              // Restore question progress
              const q = backup.questions || {};
              set({
                userAnswers: q.userAnswers || {},
                flaggedQuestions: new Set(q.flaggedQuestions || []),
                confidenceRatings: q.confidenceRatings || {},
                bankVersion: q.bankVersion || null,
              });
              // Restore progress store
              if (backup.progress) {
                localStorage.setItem('endoboard-progress', JSON.stringify({ state: backup.progress, version: 0 }));
              }
              resolve({
                answersRestored: Object.keys(q.userAnswers || {}).length,
                flagsRestored: (q.flaggedQuestions || []).length,
                confidenceRestored: Object.keys(q.confidenceRatings || {}).length,
              });
            } catch (err) {
              reject(err);
            }
          };
          reader.readAsText(file);
        });
      },

      getFilteredQuestions: ({ categories, difficulty, status, search } = {}) => {
        const { questions, userAnswers, flaggedQuestions } = get();
        return questions.filter((q) => {
          if (categories?.length && !categories.includes(q.category)) return false;
          if (difficulty && difficulty !== 'all' && q.difficulty.toLowerCase() !== difficulty.toLowerCase()) return false;
          if (status === 'answered' && !userAnswers[q.id]) return false;
          if (status === 'unanswered' && userAnswers[q.id]) return false;
          if (status === 'flagged' && !flaggedQuestions.has(q.id)) return false;
          if (search) {
            const s = search.toLowerCase();
            if (!q.question.toLowerCase().includes(s) && !q.category.toLowerCase().includes(s)) return false;
          }
          return true;
        });
      },

      getRandomQuestions: (n, filters = {}) => {
        const filtered = get().getFilteredQuestions(filters);
        const shuffled = [...filtered].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, n);
      },

      getCategories: () => {
        const { questions } = get();
        const cats = new Set();
        questions.forEach(q => cats.add(q.category));
        return [...cats].sort();
      },

      getStats: () => {
        const { questions, userAnswers } = get();
        const answered = Object.values(userAnswers);
        const correct = answered.filter((a) => a.isCorrect).length;
        const byCategory = {};
        const cats = new Set();
        questions.forEach(q => cats.add(q.category));
        [...cats].forEach((cat) => {
          const catQs = questions.filter((q) => q.category === cat);
          const catAnswered = catQs.filter((q) => userAnswers[q.id]);
          const catCorrect = catQs.filter((q) => userAnswers[q.id]?.isCorrect);
          byCategory[cat] = {
            total: catQs.length,
            answered: catAnswered.length,
            correct: catCorrect.length,
            mastery: catAnswered.length > 0 ? Math.round((catCorrect.length / catAnswered.length) * 100) : 0,
          };
        });
        return {
          total: questions.length,
          attempted: answered.length,
          correct,
          percentCorrect: answered.length > 0 ? Math.round((correct / answered.length) * 100) : 0,
          byCategory,
        };
      },

    }),
    {
      name: 'endoboard-questions',
      partialize: (state) => ({
        userAnswers: state.userAnswers,
        flaggedQuestions: Array.from(state.flaggedQuestions),
        confidenceRatings: state.confidenceRatings,
        bankVersion: state.bankVersion,
      }),
      merge: (persisted, current) => ({
        ...current,
        userAnswers: persisted?.userAnswers || {},
        flaggedQuestions: new Set(persisted?.flaggedQuestions || []),
        confidenceRatings: persisted?.confidenceRatings || {},
        bankVersion: persisted?.bankVersion || null,
      }),
    }
  )
);

export default useQuestionStore;
